import React, { useRef, useEffect } from 'react';
import { Dialog, DialogContent, Typography, Button, Box } from '@mui/material';
import logger from '../utils/Logger';
import { styled } from '@mui/system';

// Define interface for button objects
interface ButtonConfig {
  text: string;
  onClick: () => void;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  variant?: 'contained' | 'outlined' | 'text';
  delay?: number; // Optional delay in ms before action (for cursor effect)
  playAudio?: boolean; // Whether to play audio when clicked
}

interface PopupScreenProps {
  open: boolean;
  title: string;
  imageSrc: string;
  audioSrc: string;
  reason?: string;
  customReason?: React.ReactNode; // Added to allow custom formatting of the reason
  buttons: ButtonConfig[];
  isMobile: boolean; // Added prop for mobile detection
  isLandscape?: boolean; // Added prop for landscape detection
  children?: React.ReactNode;
}

// Create a styled Dialog component specifically for landscape mode
const TallLandscapeDialog = styled(Dialog)(() => ({
  '& .MuiDialog-paper': {
    margin: '0',
    maxHeight: 'none',
    height: '80vh', // Reduced from 90vh to make it shorter
    position: 'fixed',
    top: '3.5vh', // Increased from 5vh to position it more centrally
    left: '50%',
    transform: 'translateX(-50%)',
    width: '85vw',
    boxShadow: 'none',
    backgroundColor: 'transparent',
    overflow: 'hidden'
  },
  '& .MuiDialog-container': {
    height: '100vh',
    alignItems: 'flex-start'
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.7)'
  }
}));

const PopupScreen: React.FC<PopupScreenProps> = ({ 
  open, 
  title, 
  imageSrc, 
  audioSrc, 
  reason,
  customReason,
  buttons,
  isMobile,
  isLandscape = false, // Default to false if not provided
  children 
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  // Ensure body doesn't scroll when popup is open
  useEffect(() => {
    if (open) {
      // Prevent scrolling on body when popup is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const handleButtonClick = (button: ButtonConfig) => {
    if (button.playAudio && audioRef.current) {
      audioRef.current.play().catch(error => logger.error('Audio playback failed:', error));
    }
    
    if (button.delay && button.delay > 0) {
      document.body.classList.add('hourglass-cursor');
      setTimeout(() => {
        button.onClick();
        document.body.classList.remove('hourglass-cursor');
      }, button.delay);
    } else {
      button.onClick();
    }
  };

  // Common DialogContent for both normal and landscape mode
  const dialogContent = (
    <DialogContent 
      sx={{ 
        textAlign: 'center',
        border: isMobile && isLandscape ? '6px solid' : '8px solid',
        borderImage: 'linear-gradient(45deg, #FFD700, #8B4513, #DAA520, #CD853F, #FFD700) 1',
        boxShadow: '0 0 25px rgba(255, 215, 0, 0.8), 0 0 15px rgba(205, 133, 63, 0.6)',
        padding: isMobile && isLandscape ? '0' : isMobile ? '0' : '12px 8px 8px', // Remove padding for mobile portrait, adjusted for desktop
        borderRadius: isMobile && isLandscape ? '0' : '8px',
        background: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        flexDirection: isMobile && isLandscape ? 'row' : 'column',
        alignItems: 'center',
        overflowY: isMobile && isLandscape ? 'hidden' : 'auto',
        boxSizing: 'border-box',
        width: '100%',
        height: isMobile && isLandscape ? '100%' : 'auto',
        position: 'relative'
      }}
    >
      <audio ref={audioRef} src={audioSrc} />
      
      {/* Left side - Image container */}
      {isMobile && isLandscape ? (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '55%',
            overflow: 'hidden'
          }}
        >
          <img 
            src={imageSrc} 
            alt={title} 
            style={{ 
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block'
            }} 
          />
        </Box>
      ) : isMobile ? (
        // Mobile portrait mode - image flush with borders
        <Box
          sx={{
            width: '100%',
            overflow: 'hidden',
            padding: 0,
            margin: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '45vh',
            position: 'relative'
          }}
        >
          <img 
            src={imageSrc} 
            alt={title} 
            style={{ 
              width: '100%',
              objectFit: 'cover',
              display: 'block'
            }} 
          />
        </Box>
      ) : (
        // Desktop mode - image flush with gold border
        <Box
          sx={{
            height: 'auto',
            maxHeight: '480px',
            margin: '0 0 8px 0',
            overflow: 'hidden',
            position: 'relative',
            marginTop: '-12px', // Pull up to remove top padding
            marginLeft: '-8px', // Pull left to remove left padding
            marginRight: '-8px', // Pull right to remove right padding
            width: 'calc(100% + 16px)', // Extend full width accounting for negative margins
          }}
        >
          <img 
            src={imageSrc} 
            alt={title} 
            style={{ 
              width: '100%',
              height: 'auto',
              objectFit: 'cover',
              display: 'block',
              boxShadow: 'none'
            }} 
          />
        </Box>
      )}
      
      {/* Right side - Content container for landscape */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: isMobile && isLandscape ? '45%' : '100%',
          marginLeft: isMobile && isLandscape ? '55%' : 0,
          height: '100%',
          padding: isMobile && isLandscape ? '8px 12px' : 
                  isMobile ? '12px 12px' : // Add padding for mobile portrait content
                  '0 8px 12px 8px',
          boxSizing: 'border-box',
          marginTop: isMobile && !isLandscape ? '0' : 0, // Remove margin top in portrait mode
        }}
      >
        <Typography 
          variant="h3" 
          className="chess-title"
          sx={{ 
            margin: isMobile && isLandscape ? '0 0 8px 0' : isMobile ? '10px 0 5px' : '8px 0 6px',
            fontWeight: 900,
            fontSize: isMobile 
              ? (isLandscape ? '1.4rem' : '1.8rem') 
              : '2.3rem'
          }}
        >
          {title}
        </Typography>
        
        {/* Show either customReason or standard reason */}
        {customReason ? (
          <Box sx={{ 
            marginTop: isMobile && isLandscape ? '0' : 0.5,
            marginBottom: isMobile && isLandscape ? '15px' : undefined,
          }}>
            {customReason}
          </Box>
        ) : reason && (
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'white', 
              marginTop: isMobile && isLandscape ? '0' : 0.5,
              marginBottom: isMobile && isLandscape ? '10px' : undefined,
              fontFamily: "'Playfair Display', serif",
              fontStyle: 'italic',
              fontSize: isMobile 
                ? (isLandscape ? '0.9rem' : '1rem')
                : '1.15rem'
            }}
          >
            {reason}
          </Typography>
        )}
        
        {/* Show children only if no customReason is provided */}
        {!customReason && children && (
          <Box sx={{ 
            mt: isMobile && isLandscape ? 0.5 : 1.5,
            mb: isMobile && isLandscape ? 1 : 0, 
            maxWidth: '100%',
            overflow: 'visible'
          }}>
            {children}
          </Box>
        )}
        
        {/* Buttons container */}
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: isMobile && isLandscape ? 'column' : isMobile ? 'column' : 'row',
            justifyContent: 'center', 
            alignItems: 'center', 
            flexWrap: 'nowrap',
            gap: isMobile && isLandscape ? 1 : 2,
            marginTop: isMobile && isLandscape ? '8px' : isMobile ? '1.5' : 2,
            width: '100%',
            overflowX: 'visible',
            pb: isMobile && isLandscape ? 0 : 1,
            maxHeight: isMobile && isLandscape ? 'calc(100% - 20px)' : undefined
          }}
        >
          {buttons.map((button, index) => (
            <Button 
              key={index}
              variant={button.variant || 'contained'} 
              color={button.color || 'primary'} 
              onClick={() => handleButtonClick(button)}
              sx={{ 
                fontSize: isMobile ? '0.85rem' : '0.9rem',
                padding: isMobile && isLandscape ? '4px 10px' : '6px 12px',
                margin: isMobile && isLandscape ? '2px 0' : isMobile ? '4px 0' : 0,
                width: isMobile && !isLandscape 
                  ? '60%'
                  : isMobile && isLandscape 
                    ? (buttons.length <= 2 ? '50%' : '60%')
                    : 'auto',
                minWidth: isMobile && !isLandscape 
                  ? '60%'
                  : isMobile && isLandscape 
                    ? (buttons.length <= 2 ? '50%' : '60%') 
                    : '120px',
                maxWidth: isMobile && !isLandscape 
                  ? '60%'
                  : isMobile && isLandscape 
                    ? (buttons.length <= 2 ? '50%' : '60%') 
                    : '140px',
                height: isMobile && isLandscape ? '34px' : '38px',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {button.text}
            </Button>
          ))}
        </Box>
      </Box>
    </DialogContent>
  );

  // Use different Dialog components based on orientation
  return isMobile && isLandscape ? (
    <TallLandscapeDialog 
      open={open} 
      maxWidth={false}
    >
      {dialogContent}
    </TallLandscapeDialog>
  ) : (
    <Dialog 
      open={open} 
      maxWidth={false}
      PaperProps={{ 
        style: { 
          backgroundColor: 'transparent', 
          boxShadow: 'none',
          margin: isMobile ? '12px' : '24px', // Adjusted margin (between 8px and 16px)
          maxHeight: isMobile ? '97vh' : '96vh', // Adjusted height (between 96vh and 98vh)
          overflow: 'hidden',
          width: isMobile ? '90vw' : '520px', // Increased from 450px to 520px for larger desktop display
          maxWidth: isMobile ? '90vw' : undefined
        } 
      }}
    >
      {dialogContent}
    </Dialog>
  );
};

export default PopupScreen;
